import { t } from '@/messages/zh-TW';

/**
 * 共用對話框 ④：載入遮罩。
 *
 * 原系統函式：`showProcessing()`，`js-my/common.js:443-…`
 *   `:444-445` `document.getElementById('body-container').style.pointerEvents = 'none';`
 *              ← 鎖住整頁互動（`body-container` 是兩個 decorator 共用的 body id，
 *                 `layout/decorator.jsp:400`、`layout/promptDecorator.jsp:207`）
 *   `:447-…`   spin.js 設定：`lines: 13`、`length: 28`、`width: 14`、`radius: 42`、
 *              `color: '#000'`、`opacity: 0.5`、`speed: 1`、`trail: 60`
 *   `:25-27`   `common.showProcessing` 包裝；`:1248` `var spinner = showProcessing();`
 *              （呼叫端拿回 spinner 自己 `.stop()`）
 * **原系統的遮罩沒有任何文字，只有一個轉圈。**
 *
 * ponytail: 不引 spin.js（§3.2），用一顆 CSS `animate-spin` 圓環模擬；
 * 尺寸取 spin.js 的 `radius: 42` 換算成直徑約 84px，這是【雛形值待實機校準】。
 * 另外補一段只給讀屏的文字（原系統沒有）—— 無障礙基本盤，畫面上看不到。
 *
 * 【FC-5 E-Q1 裁決：z-index 改回對齊原系統，推翻 FC-4 C7】
 * `common.js:443 showProcessing()` 呼叫 spin.js 時帶 `zIndex: 2e9`（`:475`，
 * 原始碼註解自己寫「defaults to 二十億」）—— 原系統的語意是「載入遮罩鎖住**整頁**，
 * **含**當下開著的任何對話框」，同時 `:444-445` 把 `#body-container` 的
 * `pointer-events` 設成 `none`，物理上擋掉所有互動，對話框不可能疊在遮罩之上。
 * FC-4 criteria C7 曾經反過來要求「對話框疊在遮罩之上」，方向與原系統相反
 * （已記錄於 FC-4 report-R3.md §… 的爭議、FC-5 criteria E-Q1 裁決＝1：改回對齊原系統）。
 * 本檔的 `z-[3000]` 因此**高於**三個對話框外框的 `z-[2050]`
 * （`ModalFrame.tsx`／`LegacyContractModal.tsx`／`base/Modal.tsx`），
 * 見 `src/test-support/modal-zindex.test.tsx` 的閘門與反向哨兵。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface ProcessingOverlayProps {
  uiId: string;
  open: boolean;
}

export function ProcessingOverlay({ uiId, open }: ProcessingOverlayProps) {
  if (!open) return null;
  return (
    <div
      data-ui-id={uiId}
      role="status"
      aria-live="polite"
      // pointer-events 由這層遮罩自己吃掉，效果等同原系統把 #body-container 設成 none。
      // z-[3000]：FC-5 E-Q1 裁決，蓋過對話框的 z-[2050]（見上方檔頭說明，推翻 FC-4 C7）。
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50"
    >
      <span
        data-ui-id={`${uiId}-spinner`}
        aria-hidden="true"
        className="block h-[84px] w-[84px] animate-spin rounded-full border-[14px] border-black/50 border-t-transparent"
      />
      <span data-ui-id={`${uiId}-label`} className="sr-only">
        {t('state.loading')}
      </span>
    </div>
  );
}
